import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";

export default function ProductShowcase() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";

  return (
    <section
      className={`relative py-24 sm:py-32 ${
        isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"
      }`}
    >
      {/* Divider */}
      <div
        className={`mx-auto w-2/3 max-w-3xl h-px mb-24 sm:mb-32 bg-gradient-to-r from-transparent to-transparent ${
          isLight ? "via-black/10" : "via-white/10"
        }`}
      />

      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <div className="flex flex-col lg:flex-row justify-center items-center gap-10 lg:gap-20">
          {/* Left - Text */}
          <div className="flex flex-col items-center text-center lg:text-left lg:items-start justify-between w-full max-w-md lg:w-96">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <span
                className={`inline-block text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight select-none whitespace-nowrap ${
                  isLight ? "text-[#1c1c1e]/[0.04]" : "text-foreground/[0.04]"
                }`}
              >
                {t("showcase.bg_text")}
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className={`text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 whitespace-pre-line ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              {t("showcase.title")}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`mt-5 text-base md:text-lg ${
                isLight ? "text-[#6e6e73]" : "text-silver-400"
              }`}
            >
              {t("showcase.subtitle")}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className={`mt-4 text-sm sm:text-base leading-relaxed ${
                isLight ? "text-[#6e6e73]" : "text-silver-500"
              }`}
            >
              {t("showcase.desc")}
            </motion.p>
          </div>

          {/* Right - Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`w-full flex-1 aspect-video md:aspect-auto md:h-[480px] max-h-[60vh] flex-shrink-0 rounded-2xl overflow-hidden border ${
              isLight ? "border-black/10" : "border-white/10"
            }`}
          >
            <img
              src="/images/misc/product_showcase.jpg"
              alt={t("showcase.subtitle")}
              className="w-full h-full object-cover object-top opacity-80"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}